import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient } from '@dukene/shared';
import {
  Search,
  Plus,
  Store as StoreIcon,
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import type { Store, StoreStatus } from '@dukene/shared';

const statusIcons = {
  ACTIVE: CheckCircle,
  INACTIVE: AlertCircle,
  SUSPENDED: XCircle,
};

export default function Stores() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const queryClient = useQueryClient();

  // Fetch stores
  const { data: stores, isLoading, error } = useQuery<Store[]>({
    queryKey: ['stores', statusFilter, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      if (search) params.append('search', search);

      return apiClient.get<Store[]>(`/stores?${params}`);
    },
  });

  // Update store status
  const updateStatusMutation = useMutation({
    mutationFn: async ({ storeId, status }: { storeId: string; status: StoreStatus }) => {
      return apiClient.patch(`/stores/${storeId}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
    },
  });

  const filteredStores = stores || [];

  if (error) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4 mx-auto">
            <AlertCircle className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-black text-black mb-2">Failed to Load Stores</h2>
          <p className="text-gray-500">Please try again later</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight text-black">Stores</h1>
          <p className="text-gray-500 mt-2">Manage all stores on the platform</p>
        </div>
        <button className="inline-flex items-center justify-center h-12 px-6 text-sm font-bold rounded-xl bg-black text-white hover:bg-gray-800 transition-colors">
          <Plus className="w-4 h-4 mr-2" />
          Add Store
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-6 right-6">
            <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-16">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Active Stores</p>
            {isLoading ? (
              <div className="h-14 w-20 bg-gray-200 rounded-lg animate-pulse mt-2"></div>
            ) : (
              <p className="text-5xl font-black text-black mt-2">
                {filteredStores.filter((s) => s.status === 'ACTIVE').length}
              </p>
            )}
          </div>
        </div>

        <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-6 right-6">
            <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <AlertCircle className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-16">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Inactive Stores</p>
            {isLoading ? (
              <div className="h-14 w-20 bg-gray-200 rounded-lg animate-pulse mt-2"></div>
            ) : (
              <p className="text-5xl font-black text-black mt-2">
                {filteredStores.filter((s) => s.status === 'INACTIVE').length}
              </p>
            )}
          </div>
        </div>

        <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-6 right-6">
            <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <XCircle className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-16">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Suspended Stores</p>
            {isLoading ? (
              <div className="h-14 w-20 bg-gray-200 rounded-lg animate-pulse mt-2"></div>
            ) : (
              <p className="text-5xl font-black text-black mt-2">
                {filteredStores.filter((s) => s.status === 'SUSPENDED').length}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search stores..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-12 w-full rounded-xl border-2 border-gray-200 bg-white focus:border-black focus:outline-none pl-10 pr-4 text-sm transition-colors"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-12 rounded-xl border-2 border-gray-200 bg-white px-4 py-2 text-sm font-medium text-black focus:border-black focus:outline-none transition-colors"
          >
            <option value="ALL">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="SUSPENDED">Suspended</option>
          </select>
        </div>
      </div>

      {/* Stores Table */}
      {isLoading ? (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Store</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-gray-900 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 animate-pulse">
                {[...Array(8)].map((_, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-200"></div>
                        <div>
                          <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                          <div className="h-3 bg-gray-200 rounded w-24"></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 bg-gray-200 rounded w-40"></div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 bg-gray-200 rounded w-28"></div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-6 bg-gray-200 rounded-lg w-20"></div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <div className="h-9 bg-gray-200 rounded-lg w-16"></div>
                        <div className="h-9 bg-gray-200 rounded-lg w-20"></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : filteredStores.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-16">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4 mx-auto">
              <StoreIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-black text-black mb-2">No stores found</h3>
            <p className="text-gray-500">No stores match your current filters</p>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Store</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-gray-900 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredStores.map((store) => {
                  const StatusIcon = (statusIcons as any)[store.status] || AlertCircle;

                  return (
                    <tr key={store.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {store.logo ? (
                            <img
                              src={store.logo}
                              alt={store.name}
                              className="w-10 h-10 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <StoreIcon className="w-5 h-5 text-black" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <Link
                              to={`/stores/${store.id}`}
                              className="font-semibold text-black hover:text-gray-700 transition-colors block truncate"
                            >
                              {store.name}
                            </Link>
                            <p className="text-sm text-gray-500 truncate">
                              {store.subdomain}.dukene.com
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-700 truncate">{store.email}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {store.address ? (
                          <div className="text-sm text-gray-700">
                            {store.address.city}, {store.address.country}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold uppercase tracking-wider ${store.status === 'ACTIVE' ? 'bg-black text-white' :
                          store.status === 'SUSPENDED' ? 'bg-gray-700 text-white' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                          <StatusIcon className="w-3 h-3" />
                          {store.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700 whitespace-nowrap">
                          {format(new Date(store.createdAt), 'MMM d, yyyy')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/stores/${store.id}`}>
                            <button className="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-lg border-2 border-black text-black hover:bg-black hover:text-white transition-colors">
                              View
                            </button>
                          </Link>
                          {store.status === 'ACTIVE' ? (
                            <button
                              className="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-lg bg-gray-700 text-white hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() =>
                                updateStatusMutation.mutate({
                                  storeId: store.id,
                                  status: 'SUSPENDED' as StoreStatus,
                                })
                              }
                              disabled={updateStatusMutation.isPending}
                            >
                              Suspend
                            </button>
                          ) : (
                            <button
                              className="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-lg bg-black text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() =>
                                updateStatusMutation.mutate({
                                  storeId: store.id,
                                  status: 'ACTIVE' as StoreStatus,
                                })
                              }
                              disabled={updateStatusMutation.isPending}
                            >
                              Activate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
